import type { FC } from "react";
import type { JSX } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Button } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import type { SelectorHandlers } from "app/components/filters-bar/useSelectorTrigger";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { translate } from "app/filters/i18nFilter";
import { BasicDurations } from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import ChannelAnalysisChartContainer from "./components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { ChannelsOverviewBarChartContainer } from "./components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainer";
import { DiscoverMoreBtnWrapper } from "./components/ChannelsOverviewBarChart/StyledComponents";
import { ChannelsOverviewPoPChartContainer } from "./components/ChannelsOverviewPoPChart/ChannelsOverviewPoPChartContainer";
import MarketingChannelsPresets from "./components/MarketingChannelsPresets/MarketingChannelsPresets";
import PromoteMarketingChannelsPresets from "./components/MarketingChannelsPresets/PromoteMarketingChannelsPresets";
import { IncomingTrafficRefferalTable } from "./single/IncomingTrafficRefferalTable";

const CtaComponent: FC<{ showCTAButton: boolean; onClick: () => void }> = ({ showCTAButton, onClick }) => {
    if (showCTAButton && !swSettings.user.hasDM && swSettings.user.hasMR) {
        return (
            <RightFlexRow>
                <DiscoverMoreBtnWrapper
                    rel="noopener noreferrer"
                    onClick={onClick}
                >
                    <Button
                        type={"flat"}
                        label={translate("mmx.trafficSources.bar.discover.hook.button.text")}
                    />
                </DiscoverMoreBtnWrapper>
            </RightFlexRow>
        );
    }
    return null;
};

const getChartBarComponent = (
    isPoP: boolean,
    isTotal: boolean,
    isCategoryBenchmarkVisible: boolean,
    ctaComponent: JSX.Element,
    selectorHandlers: SelectorHandlers,
) => {
    if (isPoP) {
        return <ChannelsOverviewPoPChartContainer />;
    }
    if (isTotal) {
        return (
            <ChannelsOverviewBarChartContainer
                isCategoryBenchmarkVisible={isCategoryBenchmarkVisible}
                {...selectorHandlers}
            />
        );
    }
    return (
        <ChannelsOverviewBarChartContainer
            isCategoryBenchmarkVisible={isCategoryBenchmarkVisible}
            ctaComponent={ctaComponent}
            {...selectorHandlers}
        />
    );
};

export const MmxContainer: FC<{ showCTAButton: boolean }> = ({ showCTAButton = false }) => {
    const { params, state } = useCurrentStateAndParams();
    const { webSource, comparedDuration, duration } = params;
    const isPoP = !!comparedDuration;
    const isTotal = webSource === devicesTypes.TOTAL;
    const enableQualificationSideBar = !!state.qualificationSideBarComponent;
    const isCategoryBenchmarkVisible = duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const isCompare = isCompareMode();
    const { selectorHandlers: channelsOverviewSelectorHandlers } = useSelectorTrigger("channels-overview-bar-chart");
    const { selectorHandlers: channelAnalysisSelectorHandlers } = useSelectorTrigger("channel-analysis-chart");
    const { selectorHandlers: incomingTrafficSelectorHandlers } = useSelectorTrigger("traffic-sources");

    const trackDriDiscoverBtnClicked = () => {
        allTrackers.dispatchTrackEvent(
            "Internal Link",
            "click",
            "Bar Chart/Traffic Sources Overview/Discover More On Digital Marketing",
        );
        if (!swSettings.user.hasDM) {
            openHooksModalV5("CompetitveAnalysisOverviewHomepage");
        }
    };

    const ctaComponent = (
        <CtaComponent
            showCTAButton={showCTAButton}
            onClick={trackDriDiscoverBtnClicked}
        />
    );

    const chartBarComponent = getChartBarComponent(
        isPoP,
        isTotal,
        isCategoryBenchmarkVisible,
        ctaComponent,
        channelsOverviewSelectorHandlers,
    );
    return (
        <>
            {chartBarComponent}
            {isCompare ? <MarketingChannelsPresets /> : <PromoteMarketingChannelsPresets />}
            <ChannelAnalysisChartContainer {...channelAnalysisSelectorHandlers} />
            {!isPoP ? (
                <IncomingTrafficRefferalTable
                    enableQualificationSideBar={enableQualificationSideBar}
                    {...incomingTrafficSelectorHandlers}
                />
            ) : null}
        </>
    );
};
